import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { LoadingSpinner } from "@/components/loading-spinner"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchContent, SearchSelectInput, SuggestionSearch } from "@/components/ui/search"
import { useClickOutside } from "@/hooks/useClickOutside"
import { cn } from "@/lib/utils"
import { IcdCodeSeparator } from "@/modules/patients/detail/episode-of-care/icd-code-separator"
import { useFetchIcdCodesQuery } from "@/modules/patients/hooks/useIcdDiagnosisQuery"
import { IcdCodeSearchOption } from "@/modules/patients/libs/icd-code-search-option"
import { IcdDiagnosisFragment$data } from "@/modules/patients/services/__generated__/IcdDiagnosisFragment.graphql"

interface DiagnosisSearchProps {
  autoFocus?: boolean
  excludedIcdCodes: string[]
  onOptionSelect?: (option: IcdDiagnosisFragment$data) => void
}

const DiagnosisSearch = ({ autoFocus = false, excludedIcdCodes, onOptionSelect }: DiagnosisSearchProps) => {
  const { t } = useTranslation()

  const [searchValue, setSearchValue] = useState("")

  const searchRef = useRef<HTMLInputElement>(null)
  const searchResultRef = useRef<HTMLDivElement>(null)
  const [searchResultVisible, setSearchResultVisible] = useState(false)

  const { icdCodes, debounceSearch, isLoading } = useFetchIcdCodesQuery({
    searchType: "EPISODE_OF_CARE",
    excludedIcdCodes,
  })

  const handleChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(value)
    debounceSearch(value)
  }

  const handleClearInput = () => {
    setSearchValue("")
    debounceSearch("")
    setSearchResultVisible(true)
    searchRef.current?.focus()
  }

  const closeSearchResults = () => {
    setSearchResultVisible(false)
  }

  const handleSelectOption = (selectedOption: IcdDiagnosisFragment$data) => {
    setSearchValue("")
    onOptionSelect?.(selectedOption)
    setSearchResultVisible(false)
  }

  useClickOutside((event: MouseEvent) => {
    if (
      searchResultVisible &&
      searchResultRef.current &&
      !searchResultRef.current.contains(event.target as HTMLElement)
    ) {
      closeSearchResults()
    }
  }, searchRef)

  useEffect(() => {
    if (searchRef.current && autoFocus) {
      searchRef.current.focus()
    }
  }, [autoFocus])

  return (
    <div>
      <SuggestionSearch>
        <SearchSelectInput
          label={t("episodeOfCare.progressNote.diagnosisCode")}
          required
          value={searchValue}
          ref={searchRef}
          onChange={handleChange}
          testId="episode-of-care"
          onClear={(event) => {
            event.preventDefault()
            handleClearInput()
          }}
          onFocus={() => {
            setSearchResultVisible(true)
          }}
        />

        <SearchContent
          ref={searchResultRef}
          className={cn(searchValue.length >= 3 && searchResultVisible ? "show" : "hidden", "px-0!")}
          data-testid="list-icd10-code">
          {isLoading && (
            <div className="my-2 flex w-full items-center justify-center">
              <LoadingSpinner size={24} />
            </div>
          )}
          {!isLoading &&
            (!icdCodes || (icdCodes.commons.length === 0 && icdCodes.others.length === 0) ? (
              <div className="text-on-surface-subdued relative cursor-default px-2 text-sm select-none">
                {t("common.nothingFound")}
              </div>
            ) : (
              <ScrollArea>
                <div className="p-2">
                  {!!icdCodes.commons.length && (
                    <>
                      <div className="text-on-surface-subdued py-2.5 pl-2 text-sm leading-5 font-medium">
                        {t("episodeOfCare.common")}
                      </div>
                      {icdCodes.commons.map((option) => (
                        <IcdCodeSearchOption
                          key={option.entry.diagnosisCode}
                          option={option.entry}
                          highlights={option.highlights}
                          onSelect={handleSelectOption}
                        />
                      ))}
                    </>
                  )}
                  {!!icdCodes.others.length && (
                    <>
                      {!!icdCodes.commons.length && <IcdCodeSeparator />}
                      <div className="text-on-surface-subdued py-2.5 pl-2 text-sm leading-5 font-medium">
                        {t("episodeOfCare.other")}
                      </div>
                      {icdCodes.others.map((option) => (
                        <IcdCodeSearchOption
                          key={option.entry.diagnosisCode}
                          option={option.entry}
                          highlights={option.highlights}
                          onSelect={handleSelectOption}
                        />
                      ))}
                    </>
                  )}
                </div>
              </ScrollArea>
            ))}
        </SearchContent>
      </SuggestionSearch>
    </div>
  )
}

export { DiagnosisSearch }
